/**
 * recreateMvtSource — full MapLibre vector source recreation.
 *
 * Use this instead of `VectorTileSource.setTiles([newUrl])` when the new URL
 * may emit different features than the old one (scope switch, tenant switch,
 * filter change). MapLibre's tile cache is keyed by `(sourceId, z, x, y)` —
 * NOT by URL — so `setTiles` only invalidates pending fetches; already-rendered
 * tiles continue to paint their stale features alongside fresh ones.
 *
 * AK-118 hotfix (regression of AK-106 lock-in #4):
 *   Founder selects farm «Руно» → MapLibre still paints contour features from
 *   the previously cached company-scope tiles (fields belonging to other farms
 *   like «Русь»). Root cause: `v2.tsx:550-554` swapped the URL via setTiles
 *   without evicting the cache.
 *
 * Legacy precedent — `setupMapLayers.ts:798-830::recreateFieldsSource` —
 * applies the same pattern but is hard-coded to the legacy SOURCE_IDS /
 * tileUrls / MvtScope contracts. This module is a thin, generic helper
 * consumable from both V2 (`pages/map/v2.tsx`) and any future caller.
 *
 * Order of operations (CRITICAL — MapLibre forbids removing a source while
 * layers still reference it):
 *
 *     1. snapshot all layer specs whose `source` matches the target id
 *        AND capture each layer's `beforeId` (= id of the next layer in the
 *        current style that is NOT itself being removed)
 *     2. removeLayer(...) each (in order)
 *     3. removeSource(target id)
 *     4. addSource(target id, spec_with_new_tiles)
 *     5. addLayer(spec, beforeId) for each captured layer, preserving the
 *        z-order it had before the recreate
 *
 * No-op if the source does not exist (defensive — avoids throwing during
 * teardown / when a scope-change event fires after unmount).
 *
 * AK-147 fix (2026-05-23 founder UAT): without `beforeId`, MapLibre appends
 * re-added layers to the END of `style.layers`. After a scope/date swap that
 * triggers source recreation, contour fills (z-order index 2-4 in
 * setupMapLayers) jumped to the top and visually covered labels (index 6+).
 * Capturing the `beforeId` per layer restores the original ordering. The
 * removed-set exclusion is required because layers from the same source are
 * removed together — the naive "next layer in style.layers" would point to a
 * sibling that is also about to disappear.
 */
import type {
  LayerSpecification,
  Map as MaplibreMap,
  SourceSpecification,
  VectorSourceSpecification,
} from 'maplibre-gl';

/**
 * The source spec used when adding the recreated source. We only support
 * vector sources here (the AK-118 leak is specifically about fields-mvt);
 * raster sources have a different lifecycle handled by `rasterSourceEffect`.
 *
 * `tiles` is forced from the `newUrl` argument — callers may omit it, and
 * any value they pass is overwritten (callers are not always aware of the
 * URL builder convention).
 */
export type VectorSourceSpecWithoutTiles = Omit<VectorSourceSpecification, 'tiles'> & {
  tiles?: string[];
};

export function recreateMvtSource(
  map: MaplibreMap,
  sourceId: string,
  newUrl: string,
  spec: VectorSourceSpecWithoutTiles,
): void {
  // Defensive: source might not exist yet (initial-mount race) or might
  // already be torn down (unmount race). In either case there is nothing
  // to evict.
  if (!map.getSource(sourceId)) return;

  // Snapshot layer specs referencing this source. We must read them BEFORE
  // any removeLayer call because getStyle() reflects current state.
  // For each captured layer we ALSO record its z-order anchor (`beforeId`) so
  // that step 4 can re-insert it at the exact same position.
  const style = map.getStyle();
  const allLayers = (style.layers ?? []) as LayerSpecification[];
  const targetLayers = allLayers.filter(
    (l): l is LayerSpecification =>
      'source' in l && (l as { source?: string }).source === sourceId,
  );
  const removedIds = new Set(targetLayers.map((l) => l.id));
  const layerPlacements: { spec: LayerSpecification; beforeId: string | undefined }[] =
    targetLayers.map((layer) => {
      const idx = allLayers.findIndex((l) => l.id === layer.id);
      // First subsequent layer that is NOT also being removed → our anchor.
      // If none exists, the layer was at (or moved to) the top, so beforeId
      // stays undefined and addLayer appends to the end as before.
      const next = allLayers.slice(idx + 1).find((l) => !removedIds.has(l.id));
      return { spec: { ...layer }, beforeId: next?.id };
    });

  // Step 1: removeLayer for each — MapLibre forbids removing a source that
  // still has layers attached.
  for (const { spec } of layerPlacements) {
    if (map.getLayer(spec.id)) {
      map.removeLayer(spec.id);
    }
  }

  // Step 2: removeSource — evicts the entire (sourceId, z, x, y) tile cache.
  map.removeSource(sourceId);

  // Step 3: addSource with the NEW URL. We strip any `tiles` field the
  // caller may have passed in `spec` and inject `[newUrl]` ourselves.
  const newSpec: SourceSpecification = {
    ...spec,
    tiles: [newUrl],
  } as SourceSpecification;
  map.addSource(sourceId, newSpec);

  // Step 4: re-add layer specs in the original order AND at their original
  // z-position via the captured `beforeId` anchor. Without the anchor,
  // MapLibre would append every re-added layer to the END of style.layers
  // (AK-147 regression — fields contours covered labels after scope swap).
  //
  // Edge case: the anchor layer might have been removed by a concurrent
  // teardown between snapshot and re-insert. MapLibre throws on unknown
  // `beforeId`. We fall back to a plain addLayer (append) in that case —
  // the same behaviour as pre-AK-147, which is at worst a cosmetic glitch
  // and never a crash.
  for (const { spec, beforeId } of layerPlacements) {
    if (beforeId !== undefined && !map.getLayer(beforeId)) {
      map.addLayer(spec);
    } else {
      map.addLayer(spec, beforeId);
    }
  }
}
